import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ProductId } from '../../common/ui/app-tile/app-selector/types';
import type { AppItem, AppItems } from '../../common/ui/more-apps/apps-group/apps-group-item/types';

import type { UseAppItemsStateProps, UseAppItemsStateReturnValue } from './types';

export function useAppItemsState({
	items,
	filteredItems,
	isCustomizing,
	setFilteredAppItems,
	maxSelectedApps,
}: UseAppItemsStateProps): UseAppItemsStateReturnValue {
	const [selectedApps, setSelectedApps] = useState<Set<ProductId | string>>(() => {
		const allItems = [
			...items.appItems,
			...items.atlassianAppsItems,
			...items.integrationsAppsItems,
			...items.marketplaceAppsItems,
		];

		return new Set(allItems.filter((item) => item.isSelected).map((item) => item.productId));
	});

	/**
	 * Regroup the filtered app items based on the selected apps
	 * This allows for presentational regrouping to occur more easily when the selected apps change
	 */
	const regroupedAppItemsBySelections = useMemo<AppItems>(() => {
		const filterSelectedAppItems = (item: AppItem) => selectedApps.has(item.productId);
		const appItems = filteredItems.appItems || [];
		const atlassianAppsItems = filteredItems.atlassianAppsItems || [];
		const integrationsAppsItems = filteredItems.integrationsAppsItems || [];
		const marketplaceAppsItems = filteredItems.marketplaceAppsItems || [];

		const regroupedItems = {
			appItems: [
				...appItems,
				...atlassianAppsItems.filter(filterSelectedAppItems),
				...integrationsAppsItems.filter(filterSelectedAppItems),
				...marketplaceAppsItems.filter(filterSelectedAppItems),
			],
			atlassianAppsItems: atlassianAppsItems.filter((item) => !selectedApps.has(item.productId)),
			integrationsAppsItems: integrationsAppsItems.filter(
				(item) => !selectedApps.has(item.productId),
			),
			marketplaceAppsItems: marketplaceAppsItems.filter(
				(item) => !selectedApps.has(item.productId),
			),
		};

		if (isCustomizing) {
			const mapItemsAsSelectable = (item: AppItem) => ({
				...item,
				isSelectable: item.isSelectable ?? true,
			});

			return {
				appItems: regroupedItems.appItems.map(mapItemsAsSelectable),
				atlassianAppsItems: regroupedItems.atlassianAppsItems.map(mapItemsAsSelectable),
				integrationsAppsItems: regroupedItems.integrationsAppsItems,
				marketplaceAppsItems: regroupedItems.marketplaceAppsItems,
			};
		}

		return regroupedItems;
	}, [filteredItems, isCustomizing, selectedApps]);

	useEffect(() => {
		if (isCustomizing === false) {
			setFilteredAppItems(items);
		}
	}, [items, isCustomizing, setFilteredAppItems]);

	const onToggleSelect = useCallback(
		(productId: ProductId | string) => {
			setSelectedApps((prev) => {
				const next = new Set(prev);
				if (next.has(productId)) {
					next.delete(productId);
				} else if (next.size < maxSelectedApps) {
					next.add(productId);
				}
				return next;
			});
		},
		[maxSelectedApps],
	);

	const withSelection: (items: AppItem[]) => AppItem[] = useCallback(
		(items) => items.map((item) => ({ ...item, isSelected: selectedApps.has(item.productId) })),
		[selectedApps],
	);

	const processedItems = useMemo(() => {
		if (isCustomizing) {
			return {
				appItems: withSelection(regroupedAppItemsBySelections.appItems),
				atlassianAppsItems: withSelection(regroupedAppItemsBySelections.atlassianAppsItems),
				integrationsAppsItems: withSelection(regroupedAppItemsBySelections.integrationsAppsItems),
				marketplaceAppsItems: withSelection(regroupedAppItemsBySelections.marketplaceAppsItems),
			};
		}

		return {
			appItems: filteredItems.appItems,
			atlassianAppsItems: filteredItems.atlassianAppsItems,
			integrationsAppsItems: filteredItems.integrationsAppsItems,
			marketplaceAppsItems: filteredItems.marketplaceAppsItems,
		};
	}, [
		filteredItems.appItems,
		filteredItems.atlassianAppsItems,
		filteredItems.integrationsAppsItems,
		filteredItems.marketplaceAppsItems,
		isCustomizing,
		regroupedAppItemsBySelections.appItems,
		regroupedAppItemsBySelections.atlassianAppsItems,
		regroupedAppItemsBySelections.integrationsAppsItems,
		regroupedAppItemsBySelections.marketplaceAppsItems,
		withSelection,
	]);

	return { selectedApps, processedItems, onToggleSelect };
}
